import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "../TrendKpi/TrendKpi.css";
import "../Trend/Trend.css";
import "../../../../src/index.css"
import Lf23Loader from "../../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// Helper: trimestre da numero mese (1-12)
const getTrimestre = (mese) => Math.ceil(mese / 3);

// Helper: calcola variazione % YoY
const calcYoY = (curr, prev) => {
    if (prev === null || prev === undefined || prev === 0) return null;
    return Math.round(((curr - prev) / prev) * 1000) / 10;
};

// Helper: aggrega un array di righe mensili in un oggetto KPI
// Attivi = snapshot ultimo mese, tutto il resto = somma
const aggregaKpi = (righe) => {
    if (!righe || righe.length === 0) return null;
    const ultimo = righe[righe.length - 1];
    return {
        attivi: ultimo.attivi,
        ingressi: righe.reduce((s, r) => s + r.ingressi, 0),
        uscite: righe.reduce((s, r) => s + r.uscite, 0),
        fteIngressi: Math.round(righe.reduce((s, r) => s + r.fteIngressi, 0) * 10) / 10,
        fteUscite: Math.round(righe.reduce((s, r) => s + r.fteUscite, 0) * 10) / 10,
    };
};

// Helper: formatta un valore per la cella della tabella (formato italiano)
const formatCellValue = (value, format) => {
    if (format === 'percent') {
        const v = (value === null || value === undefined) ? 0 : value;
        return `${v.toFixed(1).replace('.', ',')}%`;
    }
    if (value === null || value === undefined) return '—';
    if (format === 'decimal') return value.toFixed(1).replace('.', ',');
    return value;
};

// Helper: cerca un record aggregato dal backend per tipo, anno, trimestre
const findAggregate = (aggData, tipo, anno, trimestre) => {
    const row = aggData.find(r =>
        r.periodoTipo === tipo &&
        (anno == null || r.anno === anno) &&
        (trimestre == null || r.trimestre === trimestre)
    );
    if (!row) return null;
    return {
        attivi: row.attivi,
        ingressi: row.ingressi,
        uscite: row.uscite,
        fteIngressi: row.fteIngressi,
        fteUscite: row.fteUscite,
    };
};

// Definizione delle 10 righe KPI della tabella
const KPI_DEFS = [
    { label: 'Attivi', format: 'number', getValue: (kpi, precKpi) => kpi.attivi },
    { label: 'Attivi YoY', format: 'percent', getValue: (kpi, precKpi) => calcYoY(kpi.attivi, precKpi?.attivi) },
    { label: 'Ingressi Oper', format: 'number', getValue: (kpi) => kpi.ingressi },
    { label: 'Ingressi Oper YoY', format: 'percent', getValue: (kpi, precKpi) => calcYoY(kpi.ingressi, precKpi?.ingressi) },
    { label: 'Uscite Oper', format: 'number', getValue: (kpi) => kpi.uscite },
    { label: 'Uscite Oper YoY', format: 'percent', getValue: (kpi, precKpi) => calcYoY(kpi.uscite, precKpi?.uscite) },
    { label: 'Ingressi - Uscite', format: 'number', getValue: (kpi) => kpi.ingressi - kpi.uscite },
    { label: 'FTE Ingressi', format: 'decimal', getValue: (kpi) => kpi.fteIngressi },
    { label: 'FTE Uscite', format: 'decimal', getValue: (kpi) => kpi.fteUscite },
    { label: 'FTE Delta', format: 'decimal', getValue: (kpi) => Math.round((kpi.fteIngressi - kpi.fteUscite) * 10) / 10 },
];

const TrendKpi = ({ filters, loading: parentLoading }) => {

    // stati dati dal backend
    const [monthlyData, setMonthlyData] = useState([]);
    const [aggregateData, setAggregateData] = useState([]);
    const [donutTipologia, setDonutTipologia] = useState([]);
    const [donutCessazioni, setDonutCessazioni] = useState([]);

    const [dataLoading, setDataLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [tableViewType, setTableViewType] = useState('mese');
    const [donutFilter, setDonutFilter] = useState(null);

    const [isFullscreenTipologia, setIsFullscreenTipologia] = useState(false);
    const [isFullscreenCessazioni, setIsFullscreenCessazioni] = useState(false);
    const [isFullscreenTable, setIsFullscreenTable] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenTipologia(false);
                setIsFullscreenCessazioni(false);
                setIsFullscreenTable(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    // Quando i filtri cambiano, mostra subito il loading
    useEffect(() => {
        setFilterLoading(true);
    }, [filters]);

    // Fetch dati dal backend
    useEffect(() => {
        const fetchData = async () => {
            setDataLoading(true);
            try {
                const params = {
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    settore: filters.settore?.map(s => s.value).join(",") || "",
                    tipoContratto: filters.tipoContratto?.map(t => t.value).join(",") || "",
                    ccnl: filters.ccnl?.map(c => c.value).join(",") || "",
                    motivoCessazione: filters.motivoCessazione?.map(m => m.value).join(",") || "",
                    donutFilterType: donutFilter?.type || "",
                    donutFilterValue: donutFilter?.value || ""
                };
                const res = await axios.get(`${API_URL}/hr-generale/trend-kpi`, { params });
                setMonthlyData(res.data.monthlyData || []);
                setAggregateData(res.data.aggregateData || []);
                setDonutTipologia(res.data.donutTipologia || []);
                setDonutCessazioni(res.data.donutCessazioni || []);
            } catch (error) {
                console.error("Errore nel caricamento dati Trend KPI:", error);
            } finally {
                setDataLoading(false);
                setFilterLoading(false);
            }
        };
        fetchData();
    }, [API_URL, filters, donutFilter]);

    const isLoading = parentLoading || filterLoading;

    // palette moderna con gradienti verticali per le fette dei donut
    const MODERN_PIE_GRADIENTS = [
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#60A5FA' }, { offset: 1, color: '#2563EB' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#A78BFA' }, { offset: 1, color: '#7C3AED' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#F472B6' }, { offset: 1, color: '#DB2777' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#FBBF24' }, { offset: 1, color: '#D97706' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#34D399' }, { offset: 1, color: '#059669' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#22D3EE' }, { offset: 1, color: '#0891B2' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#818CF8' }, { offset: 1, color: '#4F46E5' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#FB923C' }, { offset: 1, color: '#EA580C' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#2DD4BF' }, { offset: 1, color: '#0D9488' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#C084FC' }, { offset: 1, color: '#9333EA' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#F87171' }, { offset: 1, color: '#DC2626' }] },
        { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#A3E635' }, { offset: 1, color: '#65A30D' }] }
    ];

    // factory per costruire l'option di un donut moderno (totale centrale dinamico)
    const buildModernDonutOption = ({ seriesName, sourceData, filterType, defaultLabel }) => {
        const totale = sourceData.reduce((sum, t) => sum + Number(t.value || 0), 0);
        const isFiltered = donutFilter && donutFilter.type === filterType;
        const selectedValue = isFiltered
            ? Number(sourceData.find(t => t.name === donutFilter.value)?.value || 0)
            : totale;
        const centerMainText = String(selectedValue);
        const centerSubText = isFiltered
            ? `${donutFilter.value} · ${totale > 0 ? ((selectedValue / totale) * 100).toFixed(1) : 0}%`
            : defaultLabel;

        return {
            color: MODERN_PIE_GRADIENTS,
            tooltip: {
                trigger: 'item',
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                borderColor: 'transparent',
                borderRadius: 10,
                padding: [10, 14],
                textStyle: { color: '#F8FAFC', fontSize: 12, fontWeight: 500 },
                extraCssText: 'box-shadow: 0 10px 25px rgba(0,0,0,0.25); backdrop-filter: blur(8px);',
                formatter: (params) => `
                    <div style="font-weight:700;font-size:13px;margin-bottom:4px;">${params.name}</div>
                    <div style="display:flex;align-items:center;gap:6px;">
                        <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${typeof params.color === 'string' ? params.color : (params.color?.colorStops?.[0]?.color || '#3B82F6')};"></span>
                        <span>${params.value} · ${params.percent}%</span>
                    </div>
                `
            },
            legend: {
                type: 'scroll',
                orient: 'horizontal',
                bottom: 0,
                left: 'center',
                itemWidth: 10,
                itemHeight: 10,
                itemGap: 12,
                icon: 'circle',
                textStyle: { fontSize: 11, color: '#475569', fontWeight: 500 },
                pageIconColor: '#3B82F6',
                pageTextStyle: { color: '#64748B' }
            },
            graphic: [
                {
                    type: 'text',
                    left: 'center',
                    top: '38%',
                    z: 10,
                    style: {
                        text: centerMainText,
                        fontSize: 30,
                        fontWeight: 700,
                        fill: '#0F172A',
                        textAlign: 'center',
                        textVerticalAlign: 'middle',
                        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif'
                    }
                },
                {
                    type: 'text',
                    left: 'center',
                    top: '50%',
                    z: 10,
                    style: {
                        text: centerSubText,
                        fontSize: 11,
                        fontWeight: 500,
                        fill: '#64748B',
                        textAlign: 'center',
                        textVerticalAlign: 'middle',
                        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif'
                    }
                }
            ],
            series: [{
                name: seriesName,
                type: 'pie',
                radius: ['55%', '78%'],
                center: ['50%', '45%'],
                avoidLabelOverlap: true,
                padAngle: 2,
                itemStyle: {
                    borderRadius: 8,
                    borderColor: '#ffffff',
                    borderWidth: 3,
                    shadowBlur: 10,
                    shadowColor: 'rgba(15, 23, 42, 0.12)'
                },
                data: sourceData.map(item => ({
                    ...item,
                    itemStyle: isFiltered
                        ? item.name === donutFilter.value
                            ? { opacity: 1, shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.5)' }
                            : { opacity: 0.3 }
                        : {}
                })),
                label: {
                    show: true,
                    position: 'outside',
                    formatter: '{b|{b}}\n{c|{c}} · {p|{d}%}',
                    rich: {
                        b: { color: '#1E293B', fontSize: 11, fontWeight: 600, lineHeight: 16 },
                        c: { color: '#475569', fontSize: 11, fontWeight: 500, lineHeight: 14 },
                        p: { color: '#3B82F6', fontSize: 11, fontWeight: 700, lineHeight: 14 }
                    }
                },
                labelLine: {
                    show: true,
                    length: 10,
                    length2: 12,
                    smooth: true,
                    lineStyle: { color: '#CBD5E1', width: 1 }
                },
                emphasis: {
                    scale: true,
                    scaleSize: 12,
                    itemStyle: {
                        shadowBlur: 25,
                        shadowColor: 'rgba(59, 130, 246, 0.45)'
                    },
                    label: { fontWeight: 700 }
                },
                animationType: 'scale',
                animationEasing: 'elasticOut',
                animationDuration: 900,
                animationDelay: (idx) => idx * 60
            }]
        };
    };

    // Configurazione Donut Tipologia Attivi
    const tipologiaOptions = buildModernDonutOption({
        seriesName: 'Tipologia',
        sourceData: donutTipologia,
        filterType: 'tipologia',
        defaultLabel: 'Totale attivi'
    });

    // Configurazione Donut Motivi Cessazioni
    const cessazioniOptions = buildModernDonutOption({
        seriesName: 'Motivo',
        sourceData: donutCessazioni,
        filterType: 'cessazioni',
        defaultLabel: 'Totale cessazioni'
    });

    // Handler click donut
    const handleTipologiaClick = (params) => {
        if (donutFilter && donutFilter.type === 'tipologia' && donutFilter.value === params.name) {
            setDonutFilter(null);
        } else {
            setDonutFilter({ type: 'tipologia', value: params.name });
        }
    };

    const handleCessazioniClick = (params) => {
        if (donutFilter && donutFilter.type === 'cessazioni' && donutFilter.value === params.name) {
            setDonutFilter(null);
        } else {
            setDonutFilter({ type: 'cessazioni', value: params.name });
        }
    };

    // Dati tabella trasposta: colonne = periodi, righe = KPI
    const tableData = useMemo(() => {
        if (!monthlyData || monthlyData.length === 0) {
            return { headerLevels: [], kpiRows: [] };
        }

        const fromYear = parseInt(filters.fromDate?.split('-')[0]);
        const fromMonth = parseInt(filters.fromDate?.split('-')[1]);

        // Separo correnti (range selezionato) e precedenti (anno prima, per YoY)
        const correnti = monthlyData.filter(r =>
            r.anno > fromYear || (r.anno === fromYear && r.mese >= fromMonth)
        );
        const precedenti = monthlyData.filter(r =>
            r.anno < fromYear || (r.anno === fromYear && r.mese < fromMonth)
        );

        // Mappa veloce: numero mese -> riga precedente (per YoY mensile)
        const precPerMese = {};
        precedenti.forEach(r => { precPerMese[r.mese] = r; });

        // Ogni "colonna" della tabella: { kpi, precKpi, isTotale }
        const columns = [];
        let headerLevels = [];

        // Raggruppo correnti per anno
        const perAnno = {};
        correnti.forEach(r => {
            if (!perAnno[r.anno]) perAnno[r.anno] = [];
            perAnno[r.anno].push(r);
        });
        Object.values(perAnno).forEach(arr => arr.sort((a, b) => a.mese - b.mese));
        const anni = Object.keys(perAnno).sort();

        if (tableViewType === 'mese') {
            // ── VISTA MENSILE ──
            // Header 3 livelli: Anno > Trimestre > Mesi + Totale
            const anniHeader = [];
            const trimHeader = [];
            const mesiHeader = [];

            anni.forEach(anno => {
                const mesiAnno = perAnno[anno];
                const perTrim = {};
                mesiAnno.forEach(r => {
                    const t = getTrimestre(r.mese);
                    if (!perTrim[t]) perTrim[t] = [];
                    perTrim[t].push(r);
                });

                let annoColSpan = 0;
                const trims = Object.keys(perTrim).sort((a, b) => a - b);

                trims.forEach(trim => {
                    const mesiTrim = perTrim[trim];
                    const trimColSpan = mesiTrim.length + 1;
                    annoColSpan += trimColSpan;

                    trimHeader.push({ label: `Trim ${trim}`, colSpan: trimColSpan });

                    // Una colonna per ogni mese del trimestre
                    mesiTrim.forEach(r => {
                        const kpi = aggregaKpi([r]);
                        const precR = precPerMese[r.mese];
                        const precKpi = precR ? aggregaKpi([precR]) : null;
                        columns.push({ kpi, precKpi, isTotale: false });
                        mesiHeader.push({ label: r.label.toLowerCase(), isTotale: false });
                    });

                    // Colonna Totale del trimestre (dal backend, DISTINCT corretto)
                    const kpiTot = findAggregate(aggregateData, 'trimestre', parseInt(anno), parseInt(trim)) || aggregaKpi(mesiTrim);
                    // YoY: prima prova prev_trimestre (confronto equo periodi cappati), poi fallback
                    const precKpiTot = findAggregate(aggregateData, 'prev_trimestre', parseInt(anno) - 1, parseInt(trim))
                                    || findAggregate(aggregateData, 'trimestre', parseInt(anno) - 1, parseInt(trim))
                                    || null;
                    columns.push({ kpi: kpiTot, precKpi: precKpiTot, isTotale: true });
                    mesiHeader.push({ label: 'Totale', isTotale: true });
                });

                anniHeader.push({ label: `${anno}`, colSpan: annoColSpan });
            });

            // Grand Totale (dal backend)
            const kpiGrand = findAggregate(aggregateData, 'grand_total', null, null) || null;
            const precKpiGrand = findAggregate(aggregateData, 'prev_grand_total', null, null) || null;
            columns.push({ kpi: kpiGrand, precKpi: precKpiGrand, isTotale: true });
            anniHeader.push({ label: 'Totale', colSpan: 1, rowSpan: 3, isTotale: true });
            trimHeader.push({ skipRender: true });
            mesiHeader.push({ skipRender: true });

            headerLevels = [anniHeader, trimHeader, mesiHeader];
            headerLevels.cornerLabels = ['Anno', 'Trimestre', ''];

        } else if (tableViewType === 'trimestre') {
            // ── VISTA TRIMESTRALE ──
            // Header 2 livelli: Anno > Trimestri + Totale anno, poi Grand Totale
            const anniHeader = [];
            const trimHeader = [];
            let tuttiCorrenti = [];

            anni.forEach(anno => {
                const mesiAnno = perAnno[anno];
                tuttiCorrenti = tuttiCorrenti.concat(mesiAnno);

                const perTrim = {};
                mesiAnno.forEach(r => {
                    const t = getTrimestre(r.mese);
                    if (!perTrim[t]) perTrim[t] = [];
                    perTrim[t].push(r);
                });

                const trims = Object.keys(perTrim).sort((a, b) => a - b);
                const annoColSpan = trims.length + 1;

                trims.forEach(trim => {
                    const mesiTrim = perTrim[trim];
                    // Trimestre: usa aggregato dal backend (DISTINCT corretto)
                    const kpi = findAggregate(aggregateData, 'trimestre', parseInt(anno), parseInt(trim)) || aggregaKpi(mesiTrim);
                    // YoY: prima prova prev_trimestre (confronto equo periodi cappati), poi fallback
                    const precKpi = findAggregate(aggregateData, 'prev_trimestre', parseInt(anno) - 1, parseInt(trim))
                                 || findAggregate(aggregateData, 'trimestre', parseInt(anno) - 1, parseInt(trim))
                                 || null;
                    columns.push({ kpi, precKpi, isTotale: false });
                    trimHeader.push({ label: `Trim ${trim}`, isTotale: false });
                });

                // Colonna Totale anno (dal backend)
                const kpiAnno = findAggregate(aggregateData, 'anno', parseInt(anno), null) || aggregaKpi(mesiAnno);
                // YoY: prima prova prev_anno (confronto equo periodi cappati), poi fallback
                const precKpiAnno = findAggregate(aggregateData, 'prev_anno', parseInt(anno) - 1, null)
                                 || findAggregate(aggregateData, 'anno', parseInt(anno) - 1, null)
                                 || null;
                columns.push({ kpi: kpiAnno, precKpi: precKpiAnno, isTotale: true });
                trimHeader.push({ label: 'Totale', isTotale: true });

                anniHeader.push({ label: `${anno}`, colSpan: annoColSpan });
            });

            // Grand Totale (dal backend)
            const kpiGrand = findAggregate(aggregateData, 'grand_total', null, null) || aggregaKpi(tuttiCorrenti);
            // YoY: usa prev_grand_total dal backend (stesso range -1 anno, DISTINCT corretto)
            const precKpiGrand = findAggregate(aggregateData, 'prev_grand_total', null, null) || null;
            columns.push({ kpi: kpiGrand, precKpi: precKpiGrand, isTotale: true });
            trimHeader.push({ skipRender: true });
            anniHeader.push({ label: 'Totale', colSpan: 1, rowSpan: 2, isTotale: true });

            headerLevels = [anniHeader, trimHeader];
            headerLevels.cornerLabels = ['Anno', ''];

        } else {
            // ── VISTA ANNUALE ──
            // Header 1 livello: Anni + Grand Totale
            const periodiHeader = [];
            let tuttiCorrenti = [];

            anni.forEach(anno => {
                const mesiAnno = perAnno[anno];
                tuttiCorrenti = tuttiCorrenti.concat(mesiAnno);

                // Anno: usa aggregato dal backend (DISTINCT corretto)
                const kpi = findAggregate(aggregateData, 'anno', parseInt(anno), null) || aggregaKpi(mesiAnno);
                // YoY: prima prova prev_anno (confronto equo periodi cappati), poi fallback
                const precKpi = findAggregate(aggregateData, 'prev_anno', parseInt(anno) - 1, null)
                             || findAggregate(aggregateData, 'anno', parseInt(anno) - 1, null)
                             || null;
                columns.push({ kpi, precKpi, isTotale: false });
                periodiHeader.push({ label: `${anno}`, isTotale: false });
            });

            // Grand Totale (dal backend)
            const kpiGrand = findAggregate(aggregateData, 'grand_total', null, null) || aggregaKpi(tuttiCorrenti);
            // YoY: usa prev_grand_total dal backend (stesso range -1 anno, DISTINCT corretto)
            const precKpiGrand = findAggregate(aggregateData, 'prev_grand_total', null, null) || null;
            columns.push({ kpi: kpiGrand, precKpi: precKpiGrand, isTotale: true });
            periodiHeader.push({ label: 'Totale', isTotale: true });

            headerLevels = [periodiHeader];
            headerLevels.cornerLabels = [''];
        }

        // Costruisco le 10 righe KPI usando le definizioni
        const kpiRows = KPI_DEFS.map(def => ({
            label: def.label,
            format: def.format,
            values: columns.map(col => ({
                value: def.getValue(col.kpi, col.precKpi),
                isTotale: col.isTotale
            }))
        }));

        // Post-process: per le righe numeriche i totali = somma, per le % = media
        const processedKpiRows = kpiRows.map(row => {
            const isPercent = row.format === 'percent';
            const isNumeric = row.format === 'number' || row.format === 'decimal';
            if (!isPercent && !isNumeric) return row;

            const newValues = row.values.map((cell, i, arr) => {
                if (!cell.isTotale) return cell;

                let groupValues;

                // Grand total (ultima colonna): prende TUTTI i non-totale
                if (i === arr.length - 1) {
                    groupValues = arr
                        .filter((c, idx) => idx < i && !c.isTotale && c.value !== null)
                        .map(c => c.value);
                } else {
                    // Totale intermedio: non-totale precedenti fino al totale prima
                    groupValues = [];
                    for (let j = i - 1; j >= 0; j--) {
                        if (arr[j].isTotale) break;
                        if (arr[j].value !== null) groupValues.push(arr[j].value);
                    }
                }

                if (groupValues.length === 0) return { ...cell, value: null };

                const total = groupValues.reduce((s, v) => s + v, 0);
                const result = isPercent
                    ? Math.round((total / groupValues.length) * 10) / 10
                    : Math.round(total * 10) / 10;

                return { ...cell, value: result };
            });

            return { ...row, values: newValues };
        });

        return { headerLevels, kpiRows: processedKpiRows, columns };

    }, [monthlyData, aggregateData, tableViewType, filters.fromDate]);

    // Prepara i dati per l'export Excel: appiattisce gli header multi-livello
    // in una singola riga e costruisce le righe KPI con i valori formattati
    const prepareExcelData = () => {
        const { headerLevels, kpiRows } = tableData;
        if (!kpiRows || kpiRows.length === 0) return null;

        // Appiattisco gli header multi-livello in una riga unica
        // Prendo l'ultimo livello (il più dettagliato: mesi o trimestri o anni)
        const lastLevel = headerLevels[headerLevels.length - 1];
        const headers = ['KPI'];
        lastLevel.forEach(cell => {
            if (cell.skipRender) return;
            headers.push(cell.label);
        });

        // Costruisco le righe: [label KPI, valore1, valore2, ...]
        const rows = kpiRows.map(row => {
            const riga = [row.label];
            row.values.forEach(cell => {
                if (cell.value === null || cell.value === undefined) {
                    riga.push('—');
                } else if (row.format === 'percent') {
                    riga.push(`${cell.value.toFixed(1)}%`);
                } else if (row.format === 'decimal') {
                    riga.push(cell.value.toFixed(1));
                } else {
                    riga.push(cell.value);
                }
            });
            return riga;
        });

        return { headers, rows };
    };

    // Chiama il backend per generare il file Excel e lo scarica
    const handleExportExcel = async () => {
        try {
            const excelData = prepareExcelData();
            if (!excelData) return;

            const response = await axios.post(
                `${API_URL}/hr-generale/downloadExcel`,
                { tableName: 'Trend KPI', datiDaStampare: excelData },
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'trend_kpi.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Errore durante l\'export Excel:', error);
        }
    };

    return (
        <div className="trend-kpi-container">
            {isLoading ? (
                <Lf23Loader />
            ) : (
                <>
                    {/* Sezione Donut */}
                    <div className="trend-kpi-donuts-row">
                        <div className={`trend-kpi-donut-section${isFullscreenTipologia ? ' fullscreen-trend' : ''}`}>
                            <RotateDevice active={isFullscreenTipologia} />
                            <div className="charts-title">
                                <h3 className="trend-kpi-section-title">Tipologia Attivi</h3>
                                <button
                                    className="trend-fullscreen-btn"
                                    onClick={() => setIsFullscreenTipologia(!isFullscreenTipologia)}
                                    title={isFullscreenTipologia ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenTipologia ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <ReactECharts
                                option={tipologiaOptions}
                                style={{ height: '350px', width: '100%' }}
                                onEvents={{ click: handleTipologiaClick }}
                            />
                        </div>

                        <div className={`trend-kpi-donut-section${isFullscreenCessazioni ? ' fullscreen-trend' : ''}`}>
                            <RotateDevice active={isFullscreenCessazioni} />
                            <div className="charts-title">
                                <h3 className="trend-kpi-section-title">Motivi Cessazioni</h3>
                                <button
                                    className="trend-fullscreen-btn"
                                    onClick={() => setIsFullscreenCessazioni(!isFullscreenCessazioni)}
                                    title={isFullscreenCessazioni ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenCessazioni ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <ReactECharts
                                option={cessazioniOptions}
                                style={{ height: '350px', width: '100%' }}
                                onEvents={{ click: handleCessazioniClick }}
                            />
                        </div>
                    </div>

                    {/* Sezione Tabella */}
                    <div className={`trend-kpi-table-section${isFullscreenTable ? ' fullscreen-trend' : ''}`}>
                        <RotateDevice active={isFullscreenTable} />
                        <div className="trend-kpi-table-header">
                            <h3 className="trend-kpi-section-title">Trend e Scostamenti</h3>
                            <div className="oper-hr-table-view-type-container">
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${tableViewType === 'mese' ? 'active' : ''}`}
                                    onClick={() => setTableViewType('mese')}
                                    title="Vista Mensile"
                                >
                                    <span>M</span>
                                </button>
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${tableViewType === 'trimestre' ? 'active' : ''}`}
                                    onClick={() => setTableViewType('trimestre')}
                                    title="Vista Trimestrale"
                                >
                                    <span>T</span>
                                </button>
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${tableViewType === 'anno' ? 'active' : ''}`}
                                    onClick={() => setTableViewType('anno')}
                                    title="Vista Annuale"
                                >
                                    <span>A</span>
                                </button>

                                <button
                                    className="trend-kpi-download-btn"
                                    onClick={handleExportExcel}
                                    title="Esporta in Excel"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    Esporta
                                </button>

                                <button
                                    className="trend-fullscreen-btn"
                                    onClick={() => setIsFullscreenTable(!isFullscreenTable)}
                                    title={isFullscreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenTable ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>

                            </div>
                        </div>

                        {/* Tabella trasposta: colonne = periodi, righe = KPI */}
                        <div className="trend-kpi-table-wrapper">
                            <table className="trend-kpi-table">
                                <thead>
                                    {tableData.headerLevels.map((level, levelIdx) => (
                                        <tr key={levelIdx}>
                                            {/* Prima colonna: etichetta del livello (Anno, Trimestre, ecc.) */}
                                            <th className="trend-kpi-th-corner">
                                                {tableData.headerLevels.cornerLabels?.[levelIdx] || ''}
                                            </th>
                                            {level.map((cell, cellIdx) => {
                                                if (cell.skipRender) return null;
                                                return (
                                                    <th
                                                        key={cellIdx}
                                                        colSpan={cell.colSpan || 1}
                                                        rowSpan={cell.rowSpan || 1}
                                                        className={cell.isTotale ? 'trend-kpi-th-totale' : ''}
                                                    >
                                                        {cell.label}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {tableData.kpiRows.length > 0 ? (
                                        tableData.kpiRows.map((row, rowIdx) => (
                                            <tr key={rowIdx}>
                                                <td className="trend-kpi-cell-label">{row.label}</td>
                                                {row.values.map((cell, cellIdx) => (
                                                    <td
                                                        key={cellIdx}
                                                        className={[
                                                            cell.isTotale ? 'trend-kpi-td-totale' : '',
                                                            row.format === 'percent' && cell.value !== null && cell.value < 0 ? 'trend-kpi-cell-negativo' : '',
                                                            row.format === 'percent' && cell.value !== null && cell.value > 0 ? 'trend-kpi-cell-positivo' : ''
                                                        ].filter(Boolean).join(' ')}
                                                    >
                                                        {formatCellValue(cell.value, row.format)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={100} className="trend-kpi-cell-empty">
                                                Nessun dato disponibile
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>


                    </div>

                </>
            )}
        </div>
    );
};

export default TrendKpi;